import { useEffect, useRef, useState } from 'react';
import { aiCapabilities, aiTechStack, aiResults, aiProcessSteps, ccerIntegration } from '@/mocks/aiData';

function AICapabilityCard({ capability, index }: { capability: typeof aiCapabilities[0]; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`group bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6 hover:border-firmai-green/40 transition-all duration-500 cursor-pointer relative overflow-hidden ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Hover glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-firmai-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      {/* Icon & Title */}
      <div className="relative flex items-start gap-4 mb-4">
        <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-firmai-green/20 to-firmai-blue/20 border border-firmai-green/20 group-hover:border-firmai-green/40 group-hover:from-firmai-green/30 group-hover:to-firmai-blue/30 transition-all duration-300 shrink-0">
          <span className="w-6 h-6 flex items-center justify-center text-firmai-green group-hover:scale-110 transition-transform duration-300">
            <i className={`${capability.icon} text-xl`} />
          </span>
        </div>
        <div>
          <h3 className="text-white font-semibold text-base md:text-lg group-hover:text-firmai-green transition-colors duration-300">
            {capability.title}
          </h3>
          <p className="text-firmai-muted text-xs mt-0.5">{capability.subtitle}</p>
        </div>
      </div>

      {/* Description */}
      <p className="relative text-firmai-text text-sm leading-relaxed mb-4">
        {capability.description}
      </p>

      {/* Metrics */}
      <div className="relative grid grid-cols-3 gap-2 mb-4">
        {capability.metrics.map((metric) => (
          <div key={metric.label} className="bg-white/5 rounded-lg p-2.5 text-center group-hover:bg-white/8 transition-colors duration-300">
            <div className="text-firmai-green font-bold text-sm md:text-base">{metric.value}</div>
            <div className="text-firmai-muted text-xs mt-0.5">{metric.label}</div>
          </div>
        ))}
      </div>

      {/* Highlights */}
      <div className="relative flex flex-wrap gap-2">
        {capability.highlights.map((highlight) => (
          <span
            key={highlight}
            className="px-2.5 py-1 text-xs bg-firmai-green/10 text-firmai-green rounded-full border border-firmai-green/20 group-hover:bg-firmai-green/20 transition-colors duration-300"
          >
            {highlight}
          </span>
        ))}
      </div>
    </div>
  );
}

function TechStackLayer({ layer, index }: { layer: typeof aiTechStack[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
      } transition-all duration-700`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Layer label */}
      <div className="shrink-0 w-full sm:w-28">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r ${layer.color} border ${layer.borderColor}`}>
          <span className="w-4 h-4 flex items-center justify-center text-firmai-green">
            <i className="ri-stack-line text-sm" />
          </span>
          <span className="text-white text-sm font-medium">{layer.layer}</span>
        </div>
      </div>

      {/* Items */}
      <div className="flex flex-wrap gap-2 flex-1">
        {layer.items.map((item) => (
          <span
            key={item}
            className="px-3 py-1.5 text-sm bg-white/5 text-firmai-text rounded-md border border-white/10 hover:border-firmai-green/30 hover:text-white hover:bg-white/10 transition-all duration-300"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function ProcessStep({ step, index }: { step: typeof aiProcessSteps[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`relative flex flex-col items-center text-center ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } transition-all duration-700`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Step number circle */}
      <div className="relative w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-firmai-green/20 to-firmai-blue/20 border border-firmai-green/30 mb-4 group-hover:scale-110 transition-transform duration-300">
        <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center text-firmai-green">
          <i className={`${step.icon} text-xl md:text-2xl`} />
        </span>
        {/* Connector line */}
        {index < aiProcessSteps.length - 1 && (
          <div className="hidden lg:block absolute top-1/2 -right-1/2 w-full h-px bg-gradient-to-r from-firmai-green/30 to-transparent" />
        )}
      </div>

      <div className="text-firmai-green text-xs font-bold mb-1">STEP {step.step}</div>
      <h4 className="text-white font-semibold text-sm md:text-base mb-2">{step.title}</h4>
      <p className="text-firmai-text text-xs md:text-sm leading-relaxed max-w-[200px]">{step.description}</p>
    </div>
  );
}

function CCERSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={sectionRef}
      className={`mb-20 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Section header */}
      <div className="flex flex-col items-center text-center mb-12">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-firmai-green/10 border border-firmai-green/30 text-firmai-green text-xs font-semibold tracking-wider mb-4">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-government-line text-sm" />
          </span>
          {ccerIntegration.badge}
        </span>
        <h3 className="text-white font-bold text-xl md:text-3xl mb-3">
          {ccerIntegration.title}
        </h3>
        <p className="text-firmai-text text-sm md:text-base max-w-3xl leading-relaxed">
          {ccerIntegration.description}
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {ccerIntegration.stats.map((stat, i) => (
          <div
            key={stat.label}
            className="bg-firmai-card border border-firmai-border rounded-xl p-5 text-center hover:border-firmai-green/40 transition-all duration-300"
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            <div className="text-firmai-green font-bold text-2xl md:text-3xl mb-1">{stat.value}</div>
            <div className="text-white text-sm font-medium mb-0.5">{stat.label}</div>
            <div className="text-firmai-muted text-xs">{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* Main content: flow + data types */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        {/* Left: Data flow */}
        <div className="bg-firmai-card border border-firmai-border rounded-xl p-6">
          <h4 className="text-white font-semibold text-base md:text-lg mb-6 flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center text-firmai-green">
              <i className="ri-flow-chart" />
            </span>
            CCER 数据对接流程
          </h4>
          <div className="space-y-4">
            {ccerIntegration.flowSteps.map((step, i) => (
              <div key={step.step} className="relative">
                <div className="flex items-start gap-4">
                  {/* Step indicator */}
                  <div className="shrink-0 flex flex-col items-center">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br ${step.color} border ${step.borderColor}`}>
                      <span className="w-5 h-5 flex items-center justify-center text-firmai-green">
                        <i className={`${step.icon} text-base`} />
                      </span>
                    </div>
                    {i < ccerIntegration.flowSteps.length - 1 && (
                      <div className="w-px h-6 mt-1 bg-gradient-to-b from-firmai-green/30 to-transparent" />
                    )}
                  </div>
                  {/* Content */}
                  <div className="flex-1 pb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-firmai-green text-xs font-bold">{step.step}</span>
                      <span className="text-white font-medium text-sm">{step.title}</span>
                    </div>
                    <p className="text-firmai-text text-xs leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Data types grid */}
        <div className="bg-firmai-card border border-firmai-border rounded-xl p-6">
          <h4 className="text-white font-semibold text-base md:text-lg mb-6 flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center text-firmai-green">
              <i className="ri-stack-line" />
            </span>
            支持数据类型
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ccerIntegration.dataTypes.map((type) => (
              <div
                key={type.label}
                className="group flex items-start gap-3 p-3 rounded-lg bg-white/3 border border-white/8 hover:border-firmai-green/30 hover:bg-firmai-green/5 transition-all duration-300 cursor-default"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-firmai-green/10 border border-firmai-green/20 group-hover:bg-firmai-green/20 shrink-0 transition-colors duration-300">
                  <span className="w-4 h-4 flex items-center justify-center text-firmai-green">
                    <i className={`${type.icon} text-sm`} />
                  </span>
                </div>
                <div>
                  <div className="text-white text-xs font-medium mb-0.5 group-hover:text-firmai-green transition-colors duration-300">{type.label}</div>
                  <div className="text-firmai-muted text-xs leading-relaxed">{type.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Standards compliance */}
      <div className="bg-gradient-to-r from-firmai-green/8 to-firmai-blue/8 border border-firmai-green/20 rounded-xl p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
          <div className="shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 flex items-center justify-center text-firmai-green">
                <i className="ri-verified-badge-line text-lg" />
              </span>
              <span className="text-white font-semibold text-sm md:text-base">支持标准与认证</span>
            </div>
            <p className="text-firmai-muted text-xs">全面覆盖国内外碳核算标准</p>
          </div>
          <div className="flex flex-wrap gap-2 flex-1">
            {ccerIntegration.standards.map((std) => (
              <span
                key={std}
                className="px-3 py-1.5 text-xs bg-white/5 text-firmai-text rounded-full border border-white/10 hover:border-firmai-green/30 hover:text-white hover:bg-white/10 transition-all duration-300"
              >
                {std}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultCard({ result, index }: { result: typeof aiResults[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6 hover:border-firmai-green/40 transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="flex items-center gap-3 mb-4">
        <img
          src={result.avatar}
          alt={result.name}
          className="w-10 h-10 rounded-full object-cover border border-firmai-green/30"
        />
        <div>
          <div className="text-white text-sm font-medium">{result.name}</div>
          <div className="text-firmai-muted text-xs">{result.title} · {result.company}</div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-firmai-green/10 to-firmai-blue/10 rounded-lg p-4 mb-3 border border-firmai-green/20">
        <div className="text-firmai-green font-bold text-lg md:text-xl">{result.metric}</div>
        <div className="text-firmai-text text-sm mt-1">{result.detail}</div>
      </div>

      <div className="flex items-center gap-2">
        <span className="px-2 py-0.5 text-xs bg-white/5 text-firmai-muted rounded border border-white/10">
          {result.industry}
        </span>
      </div>
    </div>
  );
}

export default function AISection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHeaderVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const header = sectionRef.current?.querySelector('.section-header');
    if (header) observer.observe(header);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="ai"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-firmai-dark overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,217,163,0.15) 1px, transparent 0)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Decorative glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-firmai-green/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        {/* Section Header */}
        <div
          className={`section-header text-center mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">
            AI 智能引擎
          </span>
          <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3">
            AI 驱动，降本增效增质
          </h2>
          <p className="text-firmai-text mt-4 max-w-3xl mx-auto text-sm md:text-base leading-relaxed">
            Firmai 自研 AI 引擎深度融合能源管理、生产优化与碳排放追踪，通过深度学习与强化学习算法，
            为企业提供从数据采集到智能决策的全链路 AI 赋能，实现能源成本降低、生产效率提升、碳排放减少的三重目标
          </p>
        </div>

        {/* AI Capabilities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-20">
          {aiCapabilities.map((capability, index) => (
            <AICapabilityCard key={capability.title} capability={capability} index={index} />
          ))}
        </div>

        {/* AI Process Flow */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-white font-semibold text-xl md:text-2xl mb-2">AI 赋能流程</h3>
            <p className="text-firmai-text text-sm md:text-base">从数据到决策，四步实现智能化管理</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {aiProcessSteps.map((step, index) => (
              <ProcessStep key={step.step} step={step} index={index} />
            ))}
          </div>
        </div>

        {/* AI Tech Stack */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-white font-semibold text-xl md:text-2xl mb-2">AI 技术架构</h3>
            <p className="text-firmai-text text-sm md:text-base">四层技术栈，支撑企业级 AI 应用</p>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {aiTechStack.map((layer, index) => (
              <TechStackLayer key={layer.layer} layer={layer} index={index} />
            ))}
          </div>
        </div>

        {/* CCER Integration Section */}
        <CCERSection />

        {/* CTA */}
        <div className="text-center">
          <a
            href="/dashboard"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap group"
          >
            查看 AI 实时演示
            <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
              <i className="ri-arrow-right-line" />
            </span>
          </a>
          <p className="text-firmai-muted text-sm mt-4">
            已有 <span className="text-firmai-green font-semibold">500+</span> 家企业通过 Firmai AI 实现降本增效
          </p>
        </div>
      </div>
    </section>
  );
}